import { useState, useMemo } from 'react';
import SectionLabel from '../common/SectionLabel';
import PipelineKPIRow from './PipelineKPIRow';
import PipelineStageCards from './PipelineStageCards';
import ProspectTable from './ProspectTable';
import ProspectForm from './ProspectForm';
import WeightedPipelineChart from './WeightedPipelineChart';
import SmartForecast from './SmartForecast';
import PipelineCoverageRatio from './PipelineCoverageRatio';
import SourcePerformanceDashboard from './SourcePerformanceDashboard';
import ConversionFunnel from './ConversionFunnel';
import ClosedWonModal from './ClosedWonModal';
import PushToPortalModal from './PushToPortalModal';
import PortalPendingTable from './PortalPendingTable';
import { usePipeline } from '../../context/PipelineContext';
import { useClients } from '../../context/ClientsContext';
import { PIPELINE_STAGES, SERVICE_TIERS, getNextStageForProspect } from '../../utils/pipelineCalculations';
import { isInternalHousehold } from '../../../lib/households.js';

function getUniqueDivisions(prospects) {
  const divs = new Set();
  prospects.forEach((p) => {
    const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
    if (div) divs.add(div);
  });
  return Array.from(divs).sort();
}

function getActiveStageOptions() {
  return PIPELINE_STAGES.filter(s => s.order >= 1 && s.order <= 5);
}

export default function PipelineForecast() {
  const { clients: clientsForHouseholds, saveOne: saveOneClient } = useClients();
  const [activeTab, setActiveTab] = useState('pipeline');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [wonModalProspect, setWonModalProspect] = useState(null); // prospect pending Won confirmation
  const [pushModalProspect, setPushModalProspect] = useState(null); // prospect pending portal push

  const { prospects, advanceStage, updateProspect } = usePipeline();

  const divisionOptions = useMemo(() => getUniqueDivisions(prospects), [prospects]);
  const stageOptions = useMemo(() => getActiveStageOptions(), []);

  // Intercept advance: if next stage is Closed Won, show the modal instead
  function handleAdvanceWithWonCheck(prospectId) {
    const prospect = prospects.find(p => p.id === prospectId);
    if (!prospect) return;
    const nextStage = getNextStageForProspect(prospect);
    if (nextStage?.id === 'closed-won') {
      setWonModalProspect(prospect);
    } else {
      advanceStage(prospectId);
    }
  }

  // When user confirms the Won modal
  function handleWonConfirm(clientData) {
    if (!wonModalProspect) return;

    // 1. Advance the prospect to Closed Won in the pipeline (always)
    advanceStage(wonModalProspect.id);

    // If "Already an existing client" was checked, skip client creation
    // and revenue addition - the client and their revenue are already
    // recorded elsewhere. Pipeline metrics still count the win.
    if (clientData.alreadyClient) {
      setWonModalProspect(null);
      return;
    }

    // 2. Add client via ClientsContext (persists to Neon + cache). The saved
    // client carries the engine-keyed revenue fields the modal derived, so the
    // revenue engine (buildPLIncome) picks it up as the correct income line.
    // No separate won-deals overlay: the client record IS the source of truth.
    try {
      // Portal-aware household link. If this prospect was already pushed to the
      // portal, link the client to that existing household (id hh-<prospectId>),
      // which flips it from 'prospect' to 'client' and clears it from "Pending
      // in portal" — instead of the modal's fabricated hh-manual-* id. For a
      // prospect never pushed to portal, the modal's id is used and the client
      // API creates the household from it. An explicitly chosen existing
      // household (not hh-manual-*) always wins.
      const modalHh = clientData.householdId;
      const householdId = (modalHh && !String(modalHh).startsWith('hh-manual-'))
        ? modalHh
        : (wonModalProspect.portalHouseholdId || modalHh);
      const newClient = {
        ...clientData,
        householdId,
        id: `client-won-${Date.now()}`,
        startDate: clientData.startDate || new Date().toISOString().split('T')[0],
        source: 'pipeline',
      };
      // Surface a failure instead of swallowing it: the prospect is already
      // marked Won above, so a silent save failure looks like a phantom win
      // with no revenue.
      saveOneClient(newClient).catch(err => {
        console.error('saveOneClient failed:', err);
        window.alert(`This deal was marked Closed Won, but creating the client record failed, so its revenue was NOT added:\n\n${err?.message || err}\n\nPlease try again, or add the client from the Clients tab.`);
      });
    } catch (e) { /* ignore */ }

    setWonModalProspect(null);
  }

  // Get all households for the modal dropdown — pulled from ClientsContext
  // (was reading localStorage 'bcc_clients' directly; that key is no longer
  // the source of truth).
  const allHouseholds = useMemo(() => {
    const groups = {};
    for (const c of clientsForHouseholds) {
      if (isInternalHousehold(c)) continue;   // hh-firm is not a client household (lib/households.js)
      const key = c.householdId || c.name?.toLowerCase();
      if (!groups[key]) groups[key] = { name: c.householdName || c.name, entries: [], id: key };
      groups[key].entries.push(c);
    }
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [clientsForHouseholds, wonModalProspect]);

  const divisionLabel = (key) => {
    const labels = { bwm: 'BWM', bts: 'BTS', bia: 'BIA' };
    return labels[key] || key.toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header + Sub-tab toggle */}
      <div className="flex items-center justify-between">
        <SectionLabel>Pipeline & Forecast</SectionLabel>
        <div className="bg-gray-100 rounded-full p-1 flex gap-1">
          <button
            onClick={() => setActiveTab('pipeline')}
            className={`px-5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
              activeTab === 'pipeline' ? 'bg-white text-navy shadow-sm' : 'text-[#888] hover:text-navy'
            }`}
          >
            Pipeline
          </button>
          <button
            onClick={() => setActiveTab('forecast')}
            className={`px-5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
              activeTab === 'forecast' ? 'bg-white text-navy shadow-sm' : 'text-[#888] hover:text-navy'
            }`}
          >
            Forecast
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-5 py-1.5 rounded-full text-[12px] font-bold transition-all ${
              activeTab === 'pending' ? 'bg-white text-navy shadow-sm' : 'text-[#888] hover:text-navy'
            }`}
          >
            Pending in portal
          </button>
        </div>
      </div>

      {/* ===== PIPELINE SUB-TAB ===== */}
      {activeTab === 'pipeline' && (
        <div className="space-y-6">
          <PipelineKPIRow />
          <PipelineStageCards
            activeStageFilter={stageFilter !== 'all' ? stageFilter : null}
            onStageFilterChange={(stageId) => setStageFilter(stageId || 'all')}
          />
          <WeightedPipelineChart />

          {/* Action bar */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => { setEditingProspect(null); setShowForm(true); }}
              className="bg-bullogic-blue text-white px-4 py-2 rounded-[4px] text-[13px] font-bold hover:bg-bullogic-blue/90 transition-colors"
            >
              + Add Prospect
            </button>
            <div className="flex gap-3">
              <select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}
                className="border border-gray-200 rounded-[4px] px-3 py-1.5 text-[12px] text-navy bg-white">
                <option value="all">All Divisions</option>
                {divisionOptions.map((div) => (
                  <option key={div} value={div}>{divisionLabel(div)}</option>
                ))}
              </select>
              <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                className="border border-gray-200 rounded-[4px] px-3 py-1.5 text-[12px] text-navy bg-white">
                <option value="all">All Stages</option>
                {stageOptions.map((stage) => (
                  <option key={stage.id} value={stage.id}>{stage.label}</option>
                ))}
              </select>
            </div>
          </div>

          {showForm && (
            <ProspectForm
              editingProspect={editingProspect}
              onClose={() => { setShowForm(false); setEditingProspect(null); }}
            />
          )}

          <ProspectTable
            prospects={prospects}
            onEdit={(p) => { setEditingProspect(p); setShowForm(true); }}
            onAdvance={handleAdvanceWithWonCheck}
            onPushToPortal={(p) => setPushModalProspect(p)}
            onViewPending={() => setActiveTab('pending')}
            divisionFilter={divisionFilter !== 'all' ? divisionFilter : ''}
            stageFilter={stageFilter !== 'all' ? stageFilter : ''}
          />

          <PipelineCoverageRatio />
          <ConversionFunnel />
          <SourcePerformanceDashboard />
        </div>
      )}

      {/* ===== FORECAST SUB-TAB ===== */}
      {activeTab === 'forecast' && (
        <SmartForecast />
      )}

      {/* ===== PENDING IN PORTAL SUB-TAB ===== */}
      {activeTab === 'pending' && (
        <PortalPendingTable />
      )}

      {/* ===== CLOSED WON MODAL ===== */}
      {wonModalProspect && (
        <ClosedWonModal
          prospect={wonModalProspect}
          existingHouseholds={allHouseholds}
          onConfirm={handleWonConfirm}
          onCancel={() => setWonModalProspect(null)}
        />
      )}

      {/* ===== PUSH TO PORTAL MODAL ===== */}
      {pushModalProspect && (
        <PushToPortalModal
          prospect={pushModalProspect}
          onCancel={() => setPushModalProspect(null)}
          onPushed={(householdId) => {
            if (householdId) updateProspect(pushModalProspect.id, { portalHouseholdId: householdId });
            setPushModalProspect(null);
            setActiveTab('pending');
          }}
        />
      )}
    </div>
  );
}
